<template>
  <div :style="{ display: $props.visible ? 'initial' : 'none' }">
    <div class="backdrop" @click="close">
      <div class="root">
        <b-container @click.stop="() => {}" class="content bg-white">
          <b-row class="bg-white">
            <b-col>
              <b-row class="pt-3">
                <b-col>
                  <h5>
                    {{ (video || {}).title }}
                    <small
                      class="text-muted"
                    >({{ currentVideoIndex + 1 }}/{{ videos.length }})</small>
                    <span class="fa fa-times float-right clickable" @click="close" />
                  </h5>
                </b-col>
              </b-row>
              <b-row>
                <b-col class="pt-2 pb-3" cols="12" sm="8">
                  <VideoDescription
                    :video="video"
                    @openTipps="isTippsModalVisible = true"
                    @openVariants="isVariantModalVisible = true"
                  />
                </b-col>
                <b-col
                  v-if="_video.alternatives && _video.alternatives.length"
                  cols="12"
                  md="4"
                  class="mb-4"
                >
                  <b-form-group
                    label-for="alternative"
                    :description="$t('PAGE_playlist_alternative_label')"
                  >
                    <b-form-select
                      id="alternative"
                      v-model="alternative"
                      :options="[
                        { value: null, text: _video.alternativeTitle },
                        ..._video.alternatives.map(alternative => ({ value: alternative.id, text: alternative.alternativeTitle }))
                      ]"
                      size="sm"
                    ></b-form-select>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
          <b-row class="playerRow">
            <player
              :files="$props.videos[0].vimeoFiles"
              :poster="$props.videos[0].image"
              @ended="showNextVideo"
              @loadstart="isLoading = true"
              @loadeddata="isLoading = false"
              @playing="isLoading = false"
              @player="p => (player = p)"
              class="player fixed-ratio"
            />
            <div v-if="isLoading" class="playerLoaderWrapper">
              <Loader class="playerLoader" />
            </div>
          </b-row>

          <b-row class="pb-5">
            <div v-for="v in videos" :key="v.id" class="line d-flex align-items-stretch bg-white">
              <div
                @click="() => setVideo(v.id)"
                :class="{
                  'head d-flex align-items-center justify-content-center': true,
                  clickable: true,
                  'bg-green': v.id === _video.id,
                }"
              >
                <b>{{ v.id + 1 }}</b>
              </div>
              <div @click="() => setVideo(v.id)" class="tail clickable">
                <b>{{ v.id === _video.id ? video.title : v.title }}</b>
                <b-collapse :id="`collapse_${v.id}`" :visible="v.id === _video.id">
                  <VideoDescription
                    :video="v.id === _video.id ? video : v"
                    @openTipps="isTippsModalVisible = true"
                    @openVariants="isVariantModalVisible = true"
                  />
                </b-collapse>
              </div>
            </div>
          </b-row>
        </b-container>
      </div>
    </div>

    <b-modal
      :visible="isVariantModalVisible"
      centered
      lazy
      @hide="isVariantModalVisible = false"
      hide-footer
    >
      <template slot="modal-header">
        <div class="container text-center w-100 mt-2">
          <h5 class="fa fa-times float-right" @click="isVariantModalVisible = false" />
          <h5>{{ $t('PAGE_playlist_variants') }}</h5>
        </div>
      </template>

      <template v-if="Boolean(_video.variants_image)">
        <img class="vt_image" :src="_video.variants_image" />
      </template>

      <vue-markdown>{{ _video.variants }}</vue-markdown>
    </b-modal>
    <b-modal
      :visible="isTippsModalVisible"
      centered
      lazy
      @hide="isTippsModalVisible = false"
      hide-footer
    >
      <template slot="modal-header">
        <div class="container text-center w-100 mt-2">
          <h5 class="fa fa-times float-right" @click="isTippsModalVisible = false" />
          <h5>{{ $t('PAGE_playlist_tipps') }}</h5>
        </div>
      </template>

      <template v-if="Boolean(_video.tipps_image)">
        <img class="vt_image" :src="_video.tipps_image" />
      </template>

      <vue-markdown>{{ _video.tipps }}</vue-markdown>
    </b-modal>
  </div>
</template>

<script>
import { pathOr } from 'lodash/fp';
import Player from '@/components/Player';
import VideoDescription from '@/elements/VideoDescription';
import Loader from '@/components/Loader';
import VueMarkdown from 'vue-markdown';

export default {
  name: 'Playlist',
  components: { Player, VideoDescription, Loader, VueMarkdown },
  data() {
    return {
      currentVideoIndex: null,
      player: null,
      isVariantModalVisible: false,
      isTippsModalVisible: false,
      isLoading: false,
      alternative: null,
    };
  },
  props: {
    videos: {},
    autoplay: {
      default: false,
    },
    visible: {
      default: false,
    },
  },
  computed: {
    _video() {
      return pathOr([], this.currentVideoIndex, this.$props.videos);
    },
    video() {
      if (
        this.alternative !== null &&
        this._video.alternatives &&
        this._video.alternatives.length
      ) {
        return this._video.alternatives[this.alternative];
      }

      return this._video;
    },
    hasNextVideo() {
      return this.$props.videos.length > this.currentVideoIndex + 1;
    },
    nextVideo() {
      return this.$props.videos[this.currentVideoIndex + 1];
    },
  },
  watch: {
    player: function() {
      const plyrEventToTrainingEvent = event =>
        this.newEvent({
          type: event.type,
          muted: event.detail.plyr.muted,
          volume: event.detail.plyr.volume,
          quality: event.detail.plyr.quality,
          currentTime: event.detail.plyr.currentTime,
        });
      this.player.on('playing', plyrEventToTrainingEvent);
      this.player.on('pause', plyrEventToTrainingEvent);
      this.player.on('seeked', plyrEventToTrainingEvent);
      this.player.on('volumechange', plyrEventToTrainingEvent);
      if (this.$props.autoplay) {
        this.setVideo(0);
        this.start();
      }
    },
    visible: function(newValue) {
      // TODO: maybe we want this to be governd by the state?
      this.$store.commit('setIsBodyScrollPrevented', newValue);

      if (!newValue) {
        this.stop();
      } else {
        this.currentVideoIndex = 0;
        this.start();
      }
    },
    video: function() {
      if (this.video) {
        this.isLoading = true;
        this.player.source = {
          type: 'video',
          title: this.video.title,
          poster: this.video.image,
          sources: this.video.vimeoFiles
            .filter(file => [540, 720, 1080, 1280].includes(file.height))
            .map(file => ({
              src: file.link,
              size: file.height,
              type: file.type,
            })),
        };
      }

      if (this.$props.autoplay) this.start();
    },
  },
  methods: {
    restart() {
      this.setVideo(0);
      this.start();
    },
    start() {
      this.player.play().catch(() => {
        setTimeout(() => this.start(), 100);
      });
    },
    stop() {
      this.player.stop();
    },
    setVideo(id) {
      this.alternative = null;
      this.newEvent({ type: 'setVideo', id });
      this.currentVideoIndex = this.$props.videos.findIndex(v => v.id === id);
    },
    showNextVideo() {
      if (this.hasNextVideo) {
        this.currentVideoIndex += 1;
        this.newEvent({ type: 'showNextVideo' });
      } else {
        this.stop();
        this.newEvent({ type: 'finished' });
        this.$emit('finished');
      }
    },
    close() {
      this.player.pause();
      this.$emit('close');
    },
    newEvent(event) {
      this.$emit('event', {
        timestamp: new Date(),
        event,
        video: {
          id: this.video.id,
          databaseId: this.video.databaseId,
          vimeoId: this.video.vimeoId,
        },
      });
    },
  },
  beforeDestroy() {
    this.player.destroy();
  },
};
</script>

<style lang="scss" scoped>
@import 'node_modules/bootstrap/scss/functions';
@import '../../theme';

.backdrop {
  background-color: rgba(0, 0, 0, 0.7);
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  overflow-y: scroll;
}
.root {
  z-index: 1020;
  margin: 0 auto;
  min-height: 100vh;
}

$footer-height: 40px;

.content {
  min-height: calc(100vh - #{$footer-height});
}

.footer {
  height: #{$footer-height};
}

.list-group {
  width: 100%;
}
.list-group-item {
  border-radius: 0px;
}

.player {
  min-width: 100%;
  padding-bottom: 56%;
}

$playlist-head-width: 50px;

.line {
  width: 100%;

  &:hover {
    background-color: theme-color('light') !important;
    transition: background 0s;
  }

  .head {
    width: #{$playlist-head-width};
    transition: background 0.5s;
  }

  .tail {
    width: calc(100% - #{$playlist-head-width} - 10px);
    padding: 5px;
  }
}

.playerRow {
  position: relative;
  background-color: rgba(0, 0, 0, 1);
}

.playerLoaderWrapper {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  text-align: center;
  z-index: 4;
  padding-top: 20%;
}

.playerLoader {
  font-size: 400%;
  color: rgba(255, 255, 255, 0.9);
}

.vt_image {
  max-width: 100%;
}
</style>



// WEBPACK FOOTER //
// src/components/Playlist/Playlist.vue